import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BrainCircuit, Loader2, TerminalSquare, Sparkles, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const inputCls =
  "w-full bg-obsidian/60 border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt transition-colors";

const AI_MODELS = [
  { id: "openai", label: "ChatGPT", sub: "GPT-5.4 · OpenAI" },
  { id: "gemini", label: "Gemini", sub: "3.1 Pro · Google" },
];

const MODES = [
  { id: "diagnosi", label: "Diagnosi", icon: BrainCircuit, desc: "Spiega codici errore e sintomi" },
  { id: "preventivo", label: "Preventivo", icon: Receipt, desc: "Stima costi di riparazione" },
];

export const DtcDecoder = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("diagnosi");
  const [form, setForm] = useState({ code: "", vehicle: "", symptoms: "" });
  const [model, setModel] = useState("openai");
  const [out, setOut] = useState("");
  const [answeredBy, setAnsweredBy] = useState(null);
  const [busy, setBusy] = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setOut("");
    setAnsweredBy(null);
  };

  const analyze = async () => {
    if (mode === "diagnosi") {
      if (form.symptoms.trim().length < 3 && form.code.trim().length < 2)
        return toast.error("Inserisci un codice errore o descrivi i sintomi");
    } else {
      if (form.vehicle.trim().length < 3) return toast.error("Inserisci il veicolo (es. Fiat Ducato 2.3 2020)");
      if (form.symptoms.trim().length < 10) return toast.error("Descrivi il problema o l'intervento (min. 10 caratteri)");
    }
    setBusy(true);
    setOut("");
    setAnsweredBy(model);
    try {
      const body =
        mode === "diagnosi"
          ? { code: form.code, vehicle: form.vehicle, symptoms: form.symptoms || `Analizza il codice ${form.code}`, model }
          : { vehicle: form.vehicle, problem: form.symptoms, dtc_code: form.code, model };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(typeof data?.detail === "string" ? data.detail : "Servizio AI non disponibile");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        setOut((o) => o + dec.decode(value, { stream: true }));
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const isPrev = mode === "preventivo";

  return (
    <section id="ai-diagnosi" data-testid="dtc-section" className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
      <motion.div {...fadeUp} className="mb-12">
        <p className="eyebrow mb-4">AI Diagnostica &amp; Preventivi</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl">
          {isPrev ? (
            <>Quanto costa ripararlo? <span className="text-volt">L'AI fa la stima.</span></>
          ) : (
            <>Hai un codice errore? <span className="text-volt">L'AI te lo spiega subito.</span></>
          )}
        </h2>
        <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-2xl">
          {isPrev
            ? "Descrivi veicolo e problema: l'AI genera un preventivo indicativo con ricambi, ore di manodopera e fascia di prezzo totale."
            : "Inserisci il codice DTC o descrivi i sintomi e ricevi una prima spiegazione semplice: gravità, cause probabili e costo indicativo."}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <motion.div {...fadeUp} className="glass-card rounded-2xl p-6 sm:p-8 space-y-3">
          <div className="grid grid-cols-2 gap-2 p-1 rounded-xl border border-white/10" data-testid="ai-mode-tabs">
            {MODES.map((m) => (
              <button
                key={m.id}
                data-testid={`ai-mode-${m.id}`}
                onClick={() => switchMode(m.id)}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg font-mono text-xs uppercase tracking-widest transition-colors ${
                  mode === m.id ? "bg-volt text-obsidian font-bold" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                <m.icon size={14} /> {m.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              data-testid="dtc-code-input"
              className={`${inputCls} font-mono uppercase`}
              placeholder={isPrev ? "Codice DTC (opzionale)" : "Codice DTC (es. P0300)"}
              maxLength={20}
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            />
            <input
              data-testid="dtc-vehicle-input"
              className={inputCls}
              placeholder={isPrev ? "Veicolo * (es. Ducato 2.3 2020)" : "Veicolo (es. Fiat Panda 1.2 2016)"}
              value={form.vehicle}
              onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))}
            />
          </div>
          <textarea
            data-testid="dtc-symptoms-input"
            className={`${inputCls} min-h-[110px] resize-y`}
            placeholder={
              isPrev
                ? "Descrivi l'intervento o il guasto * (es. sostituzione frizione e volano, spia ABS sempre accesa...)"
                : "Sintomi: spie accese, rumori, perdite di potenza, consumi anomali..."
            }
            value={form.symptoms}
            onChange={(e) => setForm((f) => ({ ...f, symptoms: e.target.value }))}
          />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1.5">
              <Sparkles size={11} className="text-volt" /> Modello AI
            </p>
            <div className="grid grid-cols-2 gap-2" data-testid="dtc-model-select">
              {AI_MODELS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`dtc-model-${m.id}`}
                  onClick={() => setModel(m.id)}
                  className={`rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                    model === m.id ? "border-volt bg-volt/10" : "border-white/10 hover:border-volt/40"
                  }`}
                >
                  <span className={`block text-sm font-semibold ${model === m.id ? "text-volt" : "text-white"}`}>{m.label}</span>
                  <span className="block font-mono text-[10px] text-slate-500 mt-0.5">{m.sub}</span>
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="dtc-analyze-button"
            onClick={analyze}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold px-6 py-3.5 rounded-full hover:bg-spark hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-volt focus:ring-offset-2 focus:ring-offset-obsidian"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : isPrev ? <Receipt size={16} /> : <BrainCircuit size={16} />}
            {busy
              ? "Elaborazione in corso..."
              : isPrev
                ? `Genera preventivo con ${AI_MODELS.find((m) => m.id === model).label}`
                : `Analizza con ${AI_MODELS.find((m) => m.id === model).label}`}
          </button>
          <p className="text-[11px] text-slate-600 leading-relaxed text-center">
            Stima indicativa generata da AI — il preventivo reale lo conferma un esperto dopo la consulenza.
          </p>
        </motion.div>

        <motion.div {...fadeUp} className="rounded-2xl border border-white/10 bg-black/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#0E1017]">
            <TerminalSquare size={14} className="text-volt" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {isPrev ? "TAT-QUOTE · Preventivatore" : "TAT-DIAG · Terminale diagnostico"}
            </span>
            {answeredBy && (
              <span className="font-mono text-[9px] uppercase tracking-wider text-spark border border-spark/30 rounded-full px-2 py-0.5" data-testid="dtc-answered-by">
                {AI_MODELS.find((m) => m.id === answeredBy)?.label}
              </span>
            )}
            <span className={`ml-auto w-2 h-2 rounded-full ${busy ? "bg-spark animate-pulse" : "bg-emerald-400"}`} />
          </div>
          <div
            data-testid="dtc-result"
            className="p-5 sm:p-6 min-h-[280px] max-h-[420px] overflow-y-auto font-mono text-[13px] leading-relaxed text-slate-300 whitespace-pre-wrap"
          >
            {out || (
              <span className="text-slate-600">
                {isPrev
                  ? "> In attesa di input...\n> Esempio: Fiat Ducato 2.3 2020 + \"sostituzione kit frizione completo\""
                  : "> In attesa di input...\n> Esempio: codice P0300 + \"il motore vibra al minimo\""}
              </span>
            )}
            {busy && <span className="inline-block w-2 h-4 bg-volt animate-pulse ml-0.5 align-middle" />}
          </div>
          {out && !busy && isPrev && (
            <div className="px-5 pb-5 pt-4 border-t border-white/10 space-y-3" data-testid="preventivo-accept-block">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
                Il preventivo ti va bene? Contattami per fare il lavoro
              </p>
              <a
                data-testid="preventivo-whatsapp-button"
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(
                  `Ciao! Ho generato un preventivo AI sul sito per ${form.vehicle}: ${form.symptoms}. Vorrei prenotare il lavoro.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-obsidian font-semibold text-sm px-5 py-3 rounded-full hover:scale-[1.02] transition-transform"
              >
                Scrivimi su WhatsApp
              </a>
              <form
                className="grid grid-cols-1 sm:grid-cols-3 gap-2"
                data-testid="preventivo-booking-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formEl = e.currentTarget;
                  const fd = new FormData(formEl);
                  const name = String(fd.get("name") || "").trim();
                  const phone = String(fd.get("phone") || "").trim();
                  const email = String(fd.get("email") || "").trim();
                  if (name.length < 2 || phone.length < 6) return toast.error("Inserisci nome e telefono");
                  try {
                    await api.post("/leads", {
                      name,
                      phone,
                      email: email || undefined,
                      vehicle: form.vehicle,
                      message: `PREVENTIVO AI ACCETTATO\nIntervento: ${form.symptoms}\n\nStima AI:\n${out.slice(0, 2500)}`,
                    });
                    toast.success("Richiesta inviata! Ti ricontatto presto per confermare il lavoro.");
                    formEl.reset();
                  } catch {
                    toast.error("Invio non riuscito. Riprova o scrivimi su WhatsApp.");
                  }
                }}
              >
                <input data-testid="preventivo-name-input" name="name" className={inputCls} placeholder="Nome *" />
                <input data-testid="preventivo-phone-input" name="phone" className={inputCls} placeholder="Telefono *" />
                <input data-testid="preventivo-email-input" name="email" type="email" className={inputCls} placeholder="Email (opzionale)" />
                <button
                  data-testid="preventivo-booking-submit"
                  type="submit"
                  className="sm:col-span-3 w-full border border-emerald-400/40 text-emerald-400 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full hover:bg-emerald-400 hover:text-obsidian transition-all duration-200"
                >
                  Accetta e prenota il lavoro
                </button>
              </form>
              <button
                data-testid="dtc-book-cta"
                onClick={() => navigate("/nuovo-ticket")}
                className="w-full border border-volt/40 text-volt font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full hover:bg-volt hover:text-obsidian transition-all duration-200"
              >
                Oppure: secondo parere da un esperto — da €35
              </button>
            </div>
          )}
          {out && !busy && !isPrev && (
            <div className="px-5 pb-5">
              <button
                data-testid="dtc-book-cta"
                onClick={() => navigate("/nuovo-ticket")}
                className="w-full border border-volt/40 text-volt font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full hover:bg-volt hover:text-obsidian transition-all duration-200"
              >
                Approfondisci con un esperto — da €35
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
